import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, MagnifyingGlassMinus, MagnifyingGlassPlus, X } from "@phosphor-icons/react";

/**
 * Full-screen zoom viewer for product images. Supports pinch/wheel zoom,
 * pan-when-zoomed, ESC/arrow-key navigation. Extracted from Shop.jsx.
 */
export default function ImageLightbox({ src, alt, onClose, onPrev, onNext, indexLabel }) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopImmediatePropagation(); onClose(); }
      else if (e.key === "ArrowLeft" && onPrev) onPrev();
      else if (e.key === "ArrowRight" && onNext) onNext();
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(4, z + 1));
      else if (e.key === "-") { setZoom((z) => Math.max(1, z - 1)); setPan({ x: 0, y: 0 }); }
    };
    window.addEventListener("keydown", onKey);
    // lock body scroll while lightbox open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext]);

  // Reset pan whenever zoom returns to 1
  useEffect(() => { if (zoom === 1) setPan({ x: 0, y: 0 }); }, [zoom]);
  // Reset zoom + pan when src changes (navigating between images)
  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [src]);

  const cycleZoom = (e) => {
    e.stopPropagation();
    setZoom((z) => (z >= 3 ? 1 : z + 1));
  };

  const onDragStart = (e) => {
    if (zoom === 1) return;
    const p = e.touches ? e.touches[0] : e;
    dragRef.current = { startX: p.clientX - pan.x, startY: p.clientY - pan.y };
  };
  const onDragMove = (e) => {
    if (!dragRef.current) return;
    const p = e.touches ? e.touches[0] : e;
    setPan({ x: p.clientX - dragRef.current.startX, y: p.clientY - dragRef.current.startY });
  };
  const onDragEnd = () => { dragRef.current = null; };

  return (
    <div
      data-testid="shop-image-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Zoomed view of ${alt}`}
      className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close zoom"
        data-testid="shop-lightbox-close"
        className="absolute top-4 right-4 z-10 w-12 h-12 flex items-center justify-center bg-white text-black hover:bg-[#B32027] hover:text-white transition-colors"
      >
        <X size={22} weight="bold" />
      </button>
      <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setZoom((z) => Math.max(1, z - 1)); }}
          disabled={zoom <= 1}
          aria-label="Zoom out"
          data-testid="shop-lightbox-zoom-out"
          className="w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/25 border border-white/25 text-white disabled:opacity-30"
        >
          <MagnifyingGlassMinus size={18} weight="bold" />
        </button>
        <span className="text-white/85 text-xs uppercase tracking-[0.25em] font-bold min-w-[3ch] text-center">{Math.round(zoom * 100)}%</span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setZoom((z) => Math.min(4, z + 1)); }}
          disabled={zoom >= 4}
          aria-label="Zoom in"
          data-testid="shop-lightbox-zoom-in"
          className="w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/25 border border-white/25 text-white disabled:opacity-30"
        >
          <MagnifyingGlassPlus size={18} weight="bold" />
        </button>
      </div>
      {indexLabel && (
        <span className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 text-[11px] uppercase tracking-[0.25em] font-bold text-white bg-black/70 border border-white/20">
          {indexLabel}
        </span>
      )}
      {onPrev && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          aria-label="Previous image"
          data-testid="shop-lightbox-prev"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/25 border border-white/25 text-white"
        >
          <ArrowLeft size={20} weight="bold" />
        </button>
      )}
      {onNext && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          aria-label="Next image"
          data-testid="shop-lightbox-next"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/25 border border-white/25 text-white"
        >
          <ArrowRight size={20} weight="bold" />
        </button>
      )}
      <div
        className="relative w-full h-full overflow-hidden touch-none"
        onMouseDown={onDragStart}
        onMouseMove={onDragMove}
        onMouseUp={onDragEnd}
        onMouseLeave={onDragEnd}
        onTouchStart={onDragStart}
        onTouchMove={onDragMove}
        onTouchEnd={onDragEnd}
      >
        <img
          src={src}
          alt={alt}
          onClick={cycleZoom}
          data-testid="shop-lightbox-image"
          className={`select-none pointer-events-auto max-w-[95vw] max-h-[95vh] object-contain mx-auto my-auto absolute inset-0 ${zoom > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}`}
          style={{
            transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom})`,
            transformOrigin: "center center",
            transition: dragRef.current ? "none" : "transform 180ms ease-out",
          }}
          draggable={false}
        />
      </div>
    </div>
  );
}
